import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StreamingAccount, ESlotStatus } from "@/services/streaming-account";
import {
  subscriptionService,
  TransferSubscriptionRequest,
} from "@/services/subscription";
import { StreamingAccountCombobox } from "@/components/shared/StreamingAccountCombobox";
import {
  Loader2,
  ArrowRightLeft,
  MonitorPlay,
  User,
  HelpCircle,
} from "lucide-react";

interface TransferSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string;
  currentSlotName: string;
  currentAccountEmail: string;
  clientName: string;
  serviceId: number;
}

export function TransferSubscriptionModal({
  open,
  onOpenChange,
  subscriptionId,
  currentSlotName,
  currentAccountEmail,
  clientName,
  serviceId,
}: TransferSubscriptionModalProps) {
  const queryClient = useQueryClient();

  const [targetAccount, setTargetAccount] = useState<StreamingAccount | null>(null);
  const [targetSlotId, setTargetSlotId] = useState("");
  const [isNotify, setIsNotify] = useState(false);

  useEffect(() => {
    if (open) {
      setTargetAccount(null);
      setTargetSlotId("");
      setIsNotify(false);
    }
  }, [open]);

  // Filtrar slots disponibles de la cuenta seleccionada
  const availableSlots = useMemo(() => {
    if (!targetAccount) return [];
    return targetAccount.slots.filter((s) => s.status === ESlotStatus.AVAILABLE);
  }, [targetAccount]);

  // Reset slot when account changes
  useEffect(() => {
    setTargetSlotId("");
  }, [targetAccount]);

  // Mutation para transferir
  const transferMutation = useMutation({
    mutationFn: (data: TransferSubscriptionRequest) =>
      subscriptionService.transfer(subscriptionId, data),
    onSuccess: () => {
      toast.success("Suscripción transferida exitosamente");
      queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
      queryClient.invalidateQueries({ queryKey: ["streaming-accounts"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleClose = () => {
    setTargetAccount(null);
    setTargetSlotId("");
    setIsNotify(false);
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!targetAccount) {
      toast.error("Selecciona una cuenta destino");
      return;
    }

    if (!targetSlotId) {
      toast.error("Selecciona un slot destino");
      return;
    }

    transferMutation.mutate({
      targetStreamingAccountId: Number(targetAccount.id),
      targetSlotId: parseInt(targetSlotId),
      isNotify,
    });
  };

  const selectedSlot = availableSlots.find((s) => s.id === parseInt(targetSlotId));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[500px]">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ArrowRightLeft className="h-5 w-5" />
            Transferir Suscripción
          </DialogTitle>
          <DialogDescription className="text-sm">
            Transferir la suscripción a otra cuenta o slot
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
            {/* Info actual */}
            <div className="p-3 bg-muted/50 rounded-lg space-y-1.5 text-sm">
              <p className="text-muted-foreground font-medium">Origen</p>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <strong>{clientName}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <MonitorPlay className="h-3.5 w-3.5 text-muted-foreground" />
                  {currentSlotName}
                </span>
                <span className="text-muted-foreground text-xs">
                  ({currentAccountEmail})
                </span>
              </div>
            </div>

            {/* Cuenta destino */}
            <div className="space-y-1.5">
              <Label className="text-sm flex items-center gap-1.5">
                <MonitorPlay className="h-3.5 w-3.5" />
                Cuenta destino
              </Label>
              <StreamingAccountCombobox
                value={targetAccount}
                onChange={setTargetAccount}
                serviceId={serviceId}
              />
            </div>

            {/* Slot destino */}
            <div className="space-y-1.5">
              <Label className="text-sm flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                Slot destino
              </Label>
              <Select
                value={targetSlotId}
                onValueChange={setTargetSlotId}
                disabled={!targetAccount}
              >
                <SelectTrigger className="h-9">
                  <SelectValue
                    placeholder={
                      targetAccount
                        ? "Selecciona un slot"
                        : "Primero selecciona una cuenta"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableSlots.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No hay slots disponibles
                    </div>
                  ) : (
                    availableSlots.map((slot) => (
                      <SelectItem key={slot.id} value={String(slot.id)}>
                        {slot.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Notificar al cliente */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="notify-client"
                checked={isNotify}
                onCheckedChange={(checked) => setIsNotify(checked === true)}
              />
              <Label htmlFor="notify-client" className="text-sm font-normal cursor-pointer">
                Notificar al cliente
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px] text-sm">
                  Al transferir la suscripción, se le enviará al cliente la información de la nueva cuenta (correo y contraseña).
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Resumen de transferencia */}
            {targetAccount && selectedSlot && (
              <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-sm font-medium text-primary mb-2">
                  Resumen de transferencia
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <span>{currentSlotName}</span>
                  <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                  <span>
                    <strong>{selectedSlot.name}</strong>
                    <span className="text-muted-foreground ml-1">
                      ({targetAccount.email})
                    </span>
                  </span>
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" size="sm" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={transferMutation.isPending || !targetAccount || !targetSlotId}
              >
                {transferMutation.isPending && (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                Transferir
              </Button>
            </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
